import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import * as _ from 'lodash';
import calibrationGeneralDictionary from 'src/app/models/tables/dictionary/calibration-general.dictionary';
import { NA_VALUE } from 'src/app/constants';
import calibrationParkingDictionary from 'src/app/models/tables/dictionary/calibration-parking.dictionary';
import calibrationOfficeCommercial from 'src/app/models/tables/dictionary/calibration-office-commercial.dictionary';
import calibrationBonusTableDictionary from 'src/app/models/tables/dictionary/calibration-bonus.dictionary';
import { ICalibrationSetModel } from 'src/app/interfaces/calibration-set-model.interface';
import { IZoningCodeModel } from 'src/app/interfaces/zoning-code-model.interface';
import ICalibrationUsesLabel = ZoneIQCalibration.ICalibrationUsesLabel;
import IAllowedUse = ZoneIQCalibration.IAllowedUse;
import IUseClass = ZoneIQCalibration.IUseClass;
import ICalibrationUsesType = ZoneIQCalibration.ICalibrationUsesType;
import IGridicsUseTag = ZoneIQCalibration.IGridicsUseTag;
import { ITableFieldDictionary } from 'src/app/interfaces/field-settings.interface';
import { DataTypes } from 'src/shared/enums/data-type.enum';

const fieldNameHeader = 'Field Name';
const dbFieldNameHeader = 'DB Field Name';
const useNameHeader = 'Use Name';
const useTypeHeader = 'Use Type';
const useClassHeader = 'Use Class';
const gridicsUseHeader = 'Gridics Use (Tag)';
const useValueHeader = 'Use Value';
const dataTypeHeader = 'Data Type';

@Injectable({
    providedIn: 'root'
})
export class ZCExportService {
    exportZoningCodeToXLSX(zoningCode: IZoningCodeModel, gridicsUseTags: IGridicsUseTag[] = []) {
        if (!zoningCode) {
            return;
        }

        const workBook = new ExcelJS.Workbook();

        this.addCalibrationSetsTabs(
            workBook,
            zoningCode.CalibrationUsesLabels,
            zoningCode.AllowedUses,
            zoningCode.UseClasses,
            zoningCode.CalibrationUsesTypes,
            gridicsUseTags,
            zoningCode.ZoneCombinations,
            'Zones',
            true
        );
        this.addCalibrationSetsTabs(
            workBook,
            zoningCode.CalibrationUsesLabels,
            zoningCode.AllowedUses,
            zoningCode.UseClasses,
            zoningCode.CalibrationUsesTypes,
            gridicsUseTags,
            zoningCode.Overlays.filter(overlay => !overlay.isBuildingTypology),
            'Overlays'
        );
        this.addCalibrationSetsTabs(
            workBook,
            zoningCode.CalibrationUsesLabels,
            zoningCode.AllowedUses,
            zoningCode.UseClasses,
            zoningCode.CalibrationUsesTypes,
            gridicsUseTags,
            zoningCode.Overlays.filter(overlay => overlay.isBuildingTypology),
            'Typologies'
        );

        this.saveWokrBookToFile(workBook, this.getFileName(zoningCode.Name));
    }

    addCalibrationSetsTabs(
        workBook: ExcelJS.Workbook,
        calibrationUsesLabels: ICalibrationUsesLabel[],
        allowedUses: IAllowedUse[],
        useClasses: IUseClass[],
        calibrationUsesTypes: ICalibrationUsesType[],
        gridicsUseTags: IGridicsUseTag[],
        calibrationSets: ICalibrationSetModel<any>[],
        calibrationSetTypeName: string,
        exportIsNotCalibrated = false
    ) {
        if (!workBook || !calibrationSetTypeName) {
            return;
        }
        this.addDataTab(
            workBook,
            calibrationSets,
            `${calibrationSetTypeName} - General`,
            'CalibrationGeneral',
            calibrationGeneralDictionary,
            exportIsNotCalibrated
        );
        this.addUsesTab(
            workBook,
            calibrationUsesLabels,
            allowedUses,
            useClasses,
            calibrationUsesTypes,
            gridicsUseTags,
            calibrationSets,
            `${calibrationSetTypeName} - Uses`
        );
        this.addDataTab(
            workBook,
            calibrationSets,
            `${calibrationSetTypeName} - Parking`,
            'CalibrationParking',
            calibrationParkingDictionary,
            exportIsNotCalibrated
        );
        this.addDataTab(
            workBook,
            calibrationSets,
            `${calibrationSetTypeName} - Use Capacity`,
            'CalibrationOfficeCommercial',
            calibrationOfficeCommercial,
            exportIsNotCalibrated
        );
        this.addDataTab(
            workBook,
            calibrationSets,
            `${calibrationSetTypeName} - Bonus`,
            'CalibrationBonus',
            calibrationBonusTableDictionary,
            exportIsNotCalibrated
        );
    }

    addDataTab(
        workBook: ExcelJS.Workbook,
        calibrationSets: ICalibrationSetModel<any>[],
        tabName: string,
        calibrationTableName: string,
        fieldsDictionary: ITableFieldDictionary,
        exportIsNotCalibrated = false
    ) {
        if (!workBook || !tabName || !calibrationTableName || !fieldsDictionary) {
            return;
        }
        const workSheet = workBook.addWorksheet(tabName);
        if (_.isEmpty(calibrationSets)) {
            return;
        }

        const headers = [
            fieldNameHeader,
            dbFieldNameHeader,
            dataTypeHeader,
            ...calibrationSets.map(calibrationSet => calibrationSet.Name)
        ];
        workSheet.addRow(headers);

        let dataRows = [];

        if (exportIsNotCalibrated) {
            dataRows.push([
                'Is Not Calibrated',
                'IsNotCalibrated',
                DataTypes.BOOLEAN,
                ...calibrationSets.map(calibrationSet => {
                    const value = _.get(calibrationSet, 'currentState.IsNotCalibrated', calibrationSet.IsNotCalibrated);
                    return this.getBooleanValueStr(DataTypes.BOOLEAN, value);
                })
            ]);
        }

        dataRows = dataRows.concat(
            _.keys(fieldsDictionary).map(key => {
                return [
                    fieldsDictionary[key].label,
                    key,
                    `${fieldsDictionary[key].type}${fieldsDictionary[key].isRequired ? ', required' : ''}`,
                    ...calibrationSets.map(calibrationSet => {
                        const value = calibrationSet[calibrationTableName].currentState[key];
                        return value === NA_VALUE ? 'N/A' : this.getBooleanValueStr(fieldsDictionary[key].type, value);
                    })
                ];
            })
        );

        workSheet.addRows(dataRows);
        this.setWorkSheetProperties(workSheet);
    }

    addUsesTab(
        workBook: ExcelJS.Workbook,
        calibrationUsesLabels: ICalibrationUsesLabel[],
        allowedUses: IAllowedUse[],
        useClasses: IUseClass[],
        calibrationUsesTypes: ICalibrationUsesType[],
        gridicsUseTags: IGridicsUseTag[],
        calibrationSets: ICalibrationSetModel<any>[],
        tabName: string
    ) {
        if (!workBook || !tabName) {
            return;
        }

        const workSheet = workBook.addWorksheet(tabName);

        if (_.isEmpty(calibrationSets)) {
            return;
        }

        const headers = [
            'Uses Label Id',
            useNameHeader,
            useTypeHeader,
            useClassHeader,
            gridicsUseHeader,
            useValueHeader,
            ...calibrationSets.map(calibrationSet => calibrationSet.Name)
        ];
        workSheet.addRow(headers);

        if (_.isEmpty(calibrationUsesLabels)) {
            return;
        }
        const validatedAllowedUses = allowedUses || [];
        const validatedUseClasses = useClasses || [];
        const validatedGridicsUseTags = gridicsUseTags || [];
        const useTypeNameById = _.fromPairs((calibrationUsesTypes || []).map(useType => [useType.Id, useType.Name]));
        const gridicsUseNameById = _.fromPairs(
            validatedGridicsUseTags.map(gridicsUseTag => [gridicsUseTag.Id, gridicsUseTag.GridicsUse])
        );
        const dataRows = [];
        calibrationUsesLabels.forEach(useLabel => {
            const useTypeName = _.get(useLabel, 'calibrationUsesType.Name') || useTypeNameById[useLabel.CalibrationUsesTypeId];
            const useClassLabel = _.get(
                validatedUseClasses.find(useClass => useClass.Id === useLabel.ClassId),
                'Label'
            );
            const gridicsUseLabel = gridicsUseNameById[_.get(useLabel, 'GridicsUseTagId')] || '';

            const allowedUsesRow: any = [
                useLabel.Id,
                useLabel.Label,
                useTypeName,
                useClassLabel,
                gridicsUseLabel,
                'Use Allowance'
            ];
            const useLimitRow: any = [
                useLabel.Id,
                useLabel.Label,
                useTypeName,
                useClassLabel,
                gridicsUseLabel,
                'Use Limit'
            ];
            const parkingRequiredRow: any = [
                useLabel.Id,
                useLabel.Label,
                useTypeName,
                useClassLabel,
                gridicsUseLabel,
                'Parking Required'
            ];
            calibrationSets.forEach(calibrationSet => {
                const useValue = calibrationSet.CalibrationUsesVals.find(
                    cuv => (cuv as any).currentState.CalibrationUsesLabelId === useLabel.Id
                );

                allowedUsesRow.push(
                    _.get(
                        validatedAllowedUses.find(
                            allowedUse => allowedUse.Id === _.get(useValue, 'currentState.AllowedUsesId')
                        ),
                        'Label'
                    )
                );
                useLimitRow.push(_.get(useValue, 'currentState.UseLimit'));
                parkingRequiredRow.push(_.get(useValue, 'currentState.ParkingRequired'));
            });

            dataRows.push(allowedUsesRow);
            dataRows.push(useLimitRow);
            dataRows.push(parkingRequiredRow);
        });

        workSheet.addRows(dataRows);
        this.setWorkSheetProperties(workSheet);
    }

    saveWokrBookToFile(workBook: ExcelJS.Workbook, fileName: string) {
        if (!workBook || !fileName) {
            return;
        }
        workBook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            saveAs(blob, `${fileName}.xlsx`);
        });
    }

    setWorkSheetProperties(workSheet: ExcelJS.Worksheet) {
        if (!workSheet) {
            return;
        }

        this.setCellsBorders(workSheet);
        this.adjustColumnWidth(workSheet);
    }

    setCellsBorders(workSheet: ExcelJS.Worksheet) {
        if (!workSheet) {
            return;
        }

        const borderStyle: any = { style: 'thin' };

        for (let i = 0; i < workSheet.actualRowCount; i++) {
            for (let j = 0; j < workSheet.actualColumnCount; j++) {
                const cell = workSheet.getCell(i + 1, j + 1);
                cell.border = {
                    ...cell.border,
                    top: borderStyle,
                    bottom: borderStyle,
                    left: borderStyle,
                    right: borderStyle
                };
            }
        }
    }

    adjustColumnWidth(workSheet: ExcelJS.Worksheet) {
        [workSheet.columns[0], workSheet.columns[1], workSheet.columns[2], workSheet.columns[3], workSheet.columns[4]]
            .filter(Boolean)
            .forEach(column => {
                const header = _.get(column, 'values[1]');
                if (!header) {
                    return;
                }
                if (
                    [
                        fieldNameHeader,
                        dbFieldNameHeader,
                        useNameHeader,
                        useTypeHeader,
                        useClassHeader,
                        gridicsUseHeader,
                        useValueHeader,
                        dataTypeHeader
                    ].indexOf(header.toString()) < 0
                ) {
                    return;
                }
                const lengths = column.values.map(v => {
                    return v ? v.toString().length : 0;
                });
                column.width = Math.max(...lengths.filter(v => typeof v === 'number'));
            });
    }

    getFileName(zoningCodeName: string) {
        return zoningCodeName
            ? zoningCodeName
                  .replace(',', '')
                  .split(' ')
                  .join('-')
            : 'zc-export';
    }

    getBooleanValueStr(type: DataTypes, value: any) {
        return type === DataTypes.BOOLEAN && !_.isNil(value) ? (value ? 'true' : 'false') : value;
    }
}
